import { rngSeed } from '@vpgc/core/rng';
import {
  FEATURE_PICKUP_STREAK,
  HOME_TILE_X,
  HOME_TILE_Y,
  PLAYER_BASE_HARVEST_POWER,
  PLAYER_BASE_SPEED,
  PLAYER_START_INVENTORY_SLOTS,
  PLAYER_START_X,
  PLAYER_START_Y,
  createEmptyResources,
  createEmptyTechLevels,
} from '../domain/content.js';
import type { BuildingState, GameState, SaveJson } from '../contract.js';
import { TEMPTRASH_SCHEMA_VERSION } from '../contract.js';
import { cloneBuilding, createInitialBuildings } from './buildings.js';
import { createInitialGooTiles } from './goo.js';

export function createInitialState(seed: number): GameState {
  return {
    schemaVersion: TEMPTRASH_SCHEMA_VERSION,
    seed: seed | 0,
    rng: rngSeed(seed),
    tick: 0,
    nextBuildingId: 1,
    player: {
      x: PLAYER_START_X,
      y: PLAYER_START_Y,
      facing: 'south',
      speed: PLAYER_BASE_SPEED,
      harvestPower: PLAYER_BASE_HARVEST_POWER,
      selectedTool: 'hands',
      toolUseTimerSec: 0,
      selectedSlot: 0,
      gather: null,
    },
    inventory: {
      slots: Array.from({ length: PLAYER_START_INVENTORY_SLOTS }, () => null),
      nextItemUid: 1,
    },
    resources: createEmptyResources(),
    features: {
      pickupStreak: FEATURE_PICKUP_STREAK,
    },
    buildings: createInitialBuildings(),
    mobileUnits: [],
    tech: {
      points: 0,
      levels: createEmptyTechLevels(),
    },
    zones: {
      awardedTech: {},
    },
    ui: {
      openPanelBuildingId: null,
      buildMenuOpen: false,
      pendingBuildType: null,
      inventoryOpen: false,
      techTreeOpen: false,
      toolsPanelOpen: false,
      debugPanelOpen: false,
      buildMenuFocus: 0,
      inventoryFocus: 0,
      buildingPanelFocus: 0,
      techTreeFocus: 0,
      toolsPanelFocus: 0,
      debugPanelFocus: 0,
      statusMessage: null,
    },
    world: {
      tileOverrides: {},
      revealedTiles: {
        [`${HOME_TILE_X},${HOME_TILE_Y}`]: true,
        '0,1': true,
      },
      gooTiles: createInitialGooTiles(seed),
      gooSpreadElapsedSec: 0,
      gooSpreadStep: 0,
    },
    streak: {
      value: 0,
      tier: 0,
      timeRemainingSec: 0,
    },
    fxEvents: [],
    stats: {
      tilesCleared: 0,
      garbageHarvested: 0,
      garbageRecycled: 0,
      buildingsBuilt: 0,
      techPointsEarned: 0,
      trashDestroyed: 0,
      gooCleaned: 0,
    },
  };
}

export function serialize(state: GameState): SaveJson {
  return {
    schemaVersion: TEMPTRASH_SCHEMA_VERSION,
    seed: state.seed,
    rng: state.rng,
    tick: state.tick,
    nextBuildingId: state.nextBuildingId,
    player: { ...state.player },
    inventory: {
      slots: state.inventory.slots.map((slot) => (slot === null ? null : { ...slot })),
      nextItemUid: state.inventory.nextItemUid,
    },
    resources: { ...state.resources },
    features: { ...state.features },
    buildings: state.buildings.map(cloneBuilding),
    mobileUnits: state.mobileUnits.map((unit) => ({
      ...unit,
      cargo: unit.cargo.map((item) => (item === null ? null : { ...item })),
    })),
    tech: { ...state.tech, levels: { ...state.tech.levels } },
    zones: {
      awardedTech: { ...state.zones.awardedTech },
    },
    ui: { ...state.ui },
    world: {
      tileOverrides: { ...state.world.tileOverrides },
      revealedTiles: { ...state.world.revealedTiles },
      gooTiles: { ...state.world.gooTiles },
      gooSpreadElapsedSec: state.world.gooSpreadElapsedSec,
      gooSpreadStep: state.world.gooSpreadStep,
    },
    streak: { ...state.streak },
    fxEvents: state.fxEvents.map((event) => ({ ...event })),
    stats: { ...state.stats },
  };
}

export function deserialize(save: SaveJson): GameState {
  const migrated = migrateSave(save);
  return {
    schemaVersion: TEMPTRASH_SCHEMA_VERSION,
    seed: migrated.seed | 0,
    rng: migrated.rng | 0,
    tick: migrated.tick | 0,
    nextBuildingId: Math.max(1, migrated.nextBuildingId ?? 1),
    player: {
      ...migrated.player,
      selectedTool: 'hands',
      toolUseTimerSec: migrated.player.toolUseTimerSec ?? 0,
      selectedSlot: migrated.player.selectedSlot ?? 0,
      gather: migrated.player.gather ?? null,
    },
    inventory: migrated.inventory ?? {
      slots: Array.from({ length: PLAYER_START_INVENTORY_SLOTS }, () => null),
      nextItemUid: 1,
    },
    resources: { ...createEmptyResources(), ...migrated.resources },
    features: { pickupStreak: migrated.features?.pickupStreak ?? FEATURE_PICKUP_STREAK },
    buildings: (migrated.buildings ?? createInitialBuildings()).map(cloneBuilding),
    mobileUnits: (migrated.mobileUnits ?? []).map((unit) => ({
      ...unit,
      cargo: unit.cargo.map((item) => (item === null ? null : { ...item })),
    })),
    tech: {
      points: migrated.tech?.points ?? 0,
      levels: { ...createEmptyTechLevels(), ...(migrated.tech?.levels ?? {}) },
    },
    zones: migrated.zones ?? {
      awardedTech: {},
    },
    ui: {
      openPanelBuildingId: migrated.ui?.openPanelBuildingId ?? null,
      buildMenuOpen: migrated.ui?.buildMenuOpen ?? false,
      pendingBuildType: migrated.ui?.pendingBuildType ?? null,
      inventoryOpen: migrated.ui?.inventoryOpen ?? false,
      techTreeOpen: migrated.ui?.techTreeOpen ?? false,
      toolsPanelOpen: false,
      debugPanelOpen: migrated.ui?.debugPanelOpen ?? false,
      buildMenuFocus: migrated.ui?.buildMenuFocus ?? 0,
      inventoryFocus: migrated.ui?.inventoryFocus ?? migrated.player.selectedSlot ?? 0,
      buildingPanelFocus: migrated.ui?.buildingPanelFocus ?? 0,
      techTreeFocus: migrated.ui?.techTreeFocus ?? 0,
      toolsPanelFocus: migrated.ui?.toolsPanelFocus ?? 0,
      debugPanelFocus: migrated.ui?.debugPanelFocus ?? 0,
      statusMessage: migrated.ui?.statusMessage ?? null,
    },
    world: {
      tileOverrides: { ...migrated.world.tileOverrides },
      revealedTiles: { ...(migrated.world.revealedTiles ?? {}) },
      gooTiles: { ...(migrated.world.gooTiles ?? createInitialGooTiles(migrated.seed)) },
      gooSpreadElapsedSec: migrated.world.gooSpreadElapsedSec ?? 0,
      gooSpreadStep: migrated.world.gooSpreadStep ?? 0,
    },
    streak: {
      value: migrated.streak?.value ?? 0,
      tier: migrated.streak?.tier ?? 0,
      timeRemainingSec: migrated.streak?.timeRemainingSec ?? 0,
    },
    fxEvents: migrated.fxEvents ?? [],
    stats: {
      tilesCleared: migrated.stats.tilesCleared,
      garbageHarvested: migrated.stats.garbageHarvested,
      garbageRecycled: migrated.stats.garbageRecycled,
      buildingsBuilt: migrated.stats.buildingsBuilt ?? 0,
      techPointsEarned: migrated.stats.techPointsEarned ?? 0,
      trashDestroyed: migrated.stats.trashDestroyed ?? 0,
      gooCleaned: migrated.stats.gooCleaned ?? 0,
    },
  };
}

export function migrateSave(save: SaveJson): SaveJson {
  if (save.schemaVersion !== TEMPTRASH_SCHEMA_VERSION) {
    throw new Error(`Unsupported save schema ${save.schemaVersion}`);
  }
  return save;
}

export function cloneBuildings(buildings: BuildingState[]): BuildingState[] {
  return buildings.map(cloneBuilding);
}
